import fetch from 'isomorphic-fetch';
import Cookies from 'js-cookie';
import parseRetryAfter from 'parse-retry-after';
import { warn } from './rollbarLogger';

function getAuthHeaders() {
  const key = 'authHeaders';
  const storedCreds =
    (global && global.localStorage && global.localStorage.getItem(key)) ||
    Cookies.get(key) ||
    "{}";

  try {
    const creds = JSON.parse(storedCreds);

    return Object.assign({}, creds, {
      "token-type": "Bearer"
    });
  }
  catch(e) {
    return {};
  }
}

/**
 * Simplified version of redux-auth's authFetch.  Allows us to remove redux-auth as a dependency from the sa-common project.
 * This just reads auth tokens out of local storage and uses them on the request if they're present.
 * The expectation is that any components from this library will be used on a page that has other mechanisms to make sure auth headers are set and the user is logged in etc.
 *
 * @param input
 * @param options
 */
function authFetch(input, options){
  // get auth headers out of

  const authHeaders = getAuthHeaders();

  return fetch(input, Object.assign({}, options, {
    headers: Object.assign({}, options.headers, authHeaders)
  }));
}

/**
 * The maximum number of rate-limit retries to be performed at the page-level
 * @type {number}
 */
const MAX_PAGE_RATE_LIMIT_RETRIES = 10;

/**
 * Page-global counter for how many rate-limit retries have been performed
 * @type {number}
 */
global.saRateLimitRetryCount = 0;

/**
 * Wait timeout before resetting global rate limit counter
 * @type {number}
 */
const RESET_RATE_LIMIT_TIMEOUT = 60000;

let scheduleResetTimeoutId;

/**
 * Resets `global.saRateLimitRetryCount` to zero on a 60s delay.
 * Subsequent will clear any previously scheduled reset and reschedule on a new 60s delay.
 */
const scheduleResetRetryCount = () => {
  if (scheduleResetTimeoutId) {
    clearTimeout(scheduleResetTimeoutId);
  }
  scheduleResetTimeoutId = setTimeout(() => {
    global.saRateLimitRetryCount = 0;
  }, RESET_RATE_LIMIT_TIMEOUT);
};

/**
 * The maximum number of rate-limit retries per request
 * @type {number}
 */
const MAX_REQUEST_RATE_LIMIT_RETRIES = 5;

/***
 * Waits for a specified amount of time, then executes a promise.
 *
 * @param ms               Time to wait in ms
 * @returns {Promise<any>}
 */
const wait = ms => new Promise(resolve => setTimeout(resolve, ms));

/***
 * Like normal fetch except if the server returns a 429 (rate limit) status code,
 * this waits for the time specified and retries.
 *
 * @param input    a url
 * @param options  options
 * @returns {*}
 */
function fetchWithRetry(input, options) {
  const {rateLimitRetries=0} = options;
  const fetch = global.useMockedFetch || authFetch;

  return fetch(input, options)
    .then(response => {
      if (response.status === 429) {
        scheduleResetRetryCount();

        const pageRetryAllowed = global.saRateLimitRetryCount < MAX_PAGE_RATE_LIMIT_RETRIES;
        const requestRetryAllowed = rateLimitRetries < MAX_REQUEST_RATE_LIMIT_RETRIES;

        if (pageRetryAllowed && requestRetryAllowed) {
          global.saRateLimitRetryCount += 1;
          const retryAfter = parseRetryAfter(response) || 1;

          console.log(`waiting ${retryAfter} seconds for ${input}`);

          return wait(retryAfter * 1000)
            .then(() => fetchWithRetry(input, {
              ...options,
              rateLimitRetries: rateLimitRetries + 1
            }));
        }

        // Report to Rollbar if retries exhausted
        warn(`RATE_LIMIT_RETRIES_EXHAUSTED - fetchWithRetry - ${input}`, {
          global_retry_count: global.saRateLimitRetryCount,
          request_retry_count: rateLimitRetries,
          resource: input
        });
      }

      return Promise.resolve(response);
    });
}

const DEFAULT_HEADERS = {
  'Accept': 'application/json',
  'Content-Type': 'application/json'
};

function getMetaContent(name) {
  let metas = document.getElementsByTagName('meta');

  for (let i=0; i<metas.length; i++) {
    if (metas[i].getAttribute("name") === name) {
      return metas[i].getAttribute("content");
    }
  }

  return "";
}

const isResponseOk = (response, {allow404=false, checkResponse}) => {
  if(checkResponse){
    return checkResponse(response);
  }

  if(allow404 && response.status === 404) {
    return true;
  }

  return response.ok;
};

const apiFetch = (mapStateToOptions=(_)=>({})) => {
  const fetch = fetchWithRetry;

  return {
    /**
     * @callback checkResponseCallback
     * @param {response} - the response received from fetch
     * @returns {Boolean} - true if the promise should be resolved, otherwise false.
     */

    /**
     *
     * @param {Object} state - mapped to options
     * @param {string} path - the path to query
     * @param {Object} options - options for the fetch query
     * @param {string} options.baseUrl - prepended to path
     * @param {checkResponseCallback} options.checkResponse
     * @param {Boolean} options.allow404 - true if 404 responses should resolve the promise, otherwise false.
     * @param {string} options.query - "&" delimited key=value params to pass to fetch
     * @returns {Promise} - Promise object represents the response from fetch
     */
    get: (state, path, options = {}) => {
      options = {
        ...mapStateToOptions(state),
        ...options
      };

      const baseUrl = options.baseUrl || '/api';

      let token = getMetaContent('csrf-token');

      if (token !== '') {
        DEFAULT_HEADERS['X-CSRF-Token'] = token;
      }

      return fetch(constructUrl(baseUrl, path, options), {
        method: 'GET',
        headers: options.headers || DEFAULT_HEADERS,
        credentials: 'same-origin'
      })
        .catch((exception) => Promise.resolve({errors: [exception]}))
        .then((response) =>
          response.json().then(json =>
            isResponseOk(response, options) ? Promise.resolve(json) : Promise.reject({
              ...(json || {}),
              response
            })
          )
        );
    },

    post: (state, path, body, options = {}) => {
      options = {
        ...mapStateToOptions(state),
        ...options
      };

      const baseUrl = options.baseUrl || '/api';

      let token = getMetaContent('csrf-token');

      if (token !== '') {
        DEFAULT_HEADERS['X-CSRF-Token'] = token;
      }

      const {headers = DEFAULT_HEADERS} = options;
      const {rawBody = null} = options;

      return fetch(constructUrl(baseUrl, path, options), {
        method: 'POST',
        headers,
        credentials: 'same-origin',
        body: body ? JSON.stringify(body) : rawBody
      })
        .catch((exception) => Promise.resolve({errors: [exception]}))
        .then((response) =>
          response.json().then(json =>
            isResponseOk(response, options) ? Promise.resolve(json) : Promise.reject({
              ...(json || {}),
              response
            })
          )
        );
    },

    put: (state, path, body, options = {}) => {
      options = {
        ...mapStateToOptions(state),
        ...options
      };

      const baseUrl = options.baseUrl || '/api';

      let token = getMetaContent('csrf-token');
      if (token !== '') {
        DEFAULT_HEADERS['X-CSRF-Token'] = token;
      }
      const {rawBody = null} = options;
      const {headers = DEFAULT_HEADERS} = options;



      return fetch(constructUrl(baseUrl, path, options), {
        method: 'PUT',
        headers,
        credentials: 'same-origin',
        body: body ? JSON.stringify(body) : rawBody
      })
        .catch((exception) => Promise.resolve({errors: [exception]}))
        .then((response) =>
          response.json().then(json =>
            isResponseOk(response, options) ? Promise.resolve(json) : Promise.reject({
              ...(json || {}),
              response
            })
          )
        );
    },

    patch: (state, path, body, options = {}) => {
      options = {
        ...mapStateToOptions(state),
        ...options
      };

      const baseUrl = options.baseUrl || '/api';

      let token = getMetaContent('csrf-token');
      if (token !== '') {
        DEFAULT_HEADERS['X-CSRF-Token'] = token;
      }

      return fetch(constructUrl(baseUrl, path, options), {
        method: 'PATCH',
        headers: options.headers || DEFAULT_HEADERS,
        credentials: 'same-origin',
        body: JSON.stringify(body)
      })
        .catch((exception) => Promise.resolve({errors: [exception]}))
        .then((response) =>
          response.json().then(json =>
            isResponseOk(response, options) ? Promise.resolve(json) : Promise.reject({
              ...(json || {}),
              response
            })
          )
        );
    },

    delete: (state, path, options = {}) => {
      options = {
        ...mapStateToOptions(state),
        ...options
      };

      const baseUrl = options.baseUrl || '/api';

      let token = getMetaContent('csrf-token');

      if (token !== '') {
        DEFAULT_HEADERS['X-CSRF-Token'] = token;
      }

      return fetch(constructUrl(baseUrl, path, options), {
        method: 'DELETE',
        credentials: 'same-origin',
        headers: options.headers || DEFAULT_HEADERS
      })
        .catch((exception) => Promise.resolve({errors: [exception]}))
        .then((response) =>
          response.json().then((json) =>
            isResponseOk(response, options) ? Promise.resolve(json) : Promise.reject({
              ...(json || {}),
              response
            })
          )
        );
    },
    bulkDelete: (state, path, body, options = {}) => {
      options = {
        ...mapStateToOptions(state),
        ...options
      };

      const baseUrl = options.baseUrl || '/api';

      let token = getMetaContent('csrf-token');

      if (token !== '') {
        DEFAULT_HEADERS['X-CSRF-Token'] = token;
      }

      return fetch(constructUrl(baseUrl, path, options), {
        method: 'DELETE',
        credentials: 'same-origin',
        headers: options.headers || DEFAULT_HEADERS,
        body: JSON.stringify(body)
      })
        .catch((exception) => Promise.resolve({ errors: [exception] }))
        .then((response) =>
          response.json()
            .then((json) =>
              isResponseOk(response, options) ? Promise.resolve(json) : Promise.reject({
                ...(json || {}),
                response
              })
            )
        );
    }
  }
};

// Fetch object that specifically makes calls to sa0 targets
const sa0Fetch = apiFetch((state) => {
  const { httpConfig={} } = state;
  const { sa0BaseUrl, baseUrl } = httpConfig;

  return {
    baseUrl: sa0BaseUrl || baseUrl
  };
});

export {
  sa0Fetch as default,
  sa0Fetch
}


// Takes an array of strings and turns them into a query string, i.e.
// arrayToQuery('foo', ['a','b','c']) => foo[]=a&foo[]=b&foo[]=c
function arrayToQuery(name, values=[]) {
  return values.map((value) => `${name}[]=${encodeURIComponent(value)}`)
    .join('&');
}

function constructUrl(baseUrl="", path="", options={}){
  const {query=""} = options;
  const {include=[]} = options;

  const urlWithPath = [baseUrl, path].join('/');

  const fullQuery = [query, arrayToQuery('include', include)]
    .filter(x=>x)
    .join('&');

  // filter(item => item) removes empty strings in the case of no options given
  return [urlWithPath, fullQuery].filter(item => item).join('?');
}

